import {Button, Input} from 'antd'
import {auth_requestResetPassword_Api} from 'apis'
import FieldDecorator from 'components/form/FieldDecorator'
import {Formik} from 'formik'
import useAsyncAction from 'modules/asyncCache/useAsyncAction'
import React, {useContext, useEffect, useState} from 'react'
import * as Yup from 'yup'
import {NullAsDefault, renderIf} from "../Shared";
import {LocalContext} from "../../modules/local/LocalContext";
import _ from "lodash";
import {BsShieldLock} from "react-icons/bs";

export const ForgetPassword = ({onBack = NullAsDefault}) => {
    const {translate: t} = useContext(LocalContext)

    const {
        response,
        isLoading,
        success,
        handleAsyncAction
    } = useAsyncAction({apiInfo: auth_requestResetPassword_Api})

    const isSucceed = Number(_.get(response, 'status')) === 200
    const errorMessage = _.get(response, 'errorMessages.error')
    const error_code = _.get(response, 'errorMessages.error_code')

    const {
        reset_pwd_wait_seconds,
        reset_pwd_status,
        reset_pwd_attempts,
    } = response?.errorMessages ?? {}

    const isPending = reset_pwd_status === 1
    const [timeLeft, setTimeLeft] = useState(Number(reset_pwd_wait_seconds) || 0);
    useEffect(() => {
        if (!isPending) return;

        let current = Number(reset_pwd_wait_seconds) || 0;
        setTimeLeft(current);

        const timer = setInterval(() => {
            current -= 1;

            setTimeLeft((prev) => {
                if (prev <= 1) {
                    clearInterval(timer);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(timer);
    }, [reset_pwd_wait_seconds, isPending]);

    return (
        <Formik
            initialValues={{email: ''}}
            onSubmit={(values, {setSubmitting, setErrors}) => {
                handleAsyncAction(values)
            }}
            validationSchema={Yup.object().shape(
                {email: Yup.string().email(t('invalid email address')).required(t('required field'))}
            )}>
            {props => {
                const {
                    values,
                    errors,
                    isSubmitting,
                    handleChange,
                    handleBlur,
                    handleSubmit
                } = props

                const getEmail = (
                    <span className="font-semibold text-blue-600">
                        {values.email}
                    </span>
                )

                return (
                    <div className="mt-8 flex flex-col flex-center gap-6">
                        <div
                            className="text-center font-bold text-2xl text-color-000 uppercase tracking-widest leading-tight">
                            {t('forgot password')}
                        </div>
                        <form onSubmit={handleSubmit} className="w-full flex flex-col gap-6">
                            <div className="flex flex-col flex-center gap-2">
                                <div className="text-center font-semibold text-color-100 tracking-wide">
                                    {t('recover login password')}
                                </div>
                                <div className="text-center text-sm text-color-300 tracking-wider">
                                    {t('recover login password description')}
                                </div>
                            </div>
                            {renderIf(
                                !isSucceed && !!error_code,
                                <div className="p-3 border-t border-color-50 shadow-md rounded-xl flex flex-col gap-3">
                                    <div className="flex items-center gap-2">
                                        <BsShieldLock size={18} className="text-blue-600" />
                                        <span className="flex-1 font-medium text-base text-blue-700">
                                            {t('recover login password')}
                                        </span>
                                    </div>
                                    {reset_pwd_status === 1 ? (
                                        <div className="flex flex-col gap-2">
                                            <p className="text-color-200 tracking-wider">
                                                {t(
                                                    'You have just sent a request. Please wait {time} before sending another request',
                                                    {
                                                        time: (
                                                            <span className="font-bold text-secondary-600 italic">
                                                                {timeLeft}
                                                            </span>
                                                        )
                                                    }
                                                )}
                                            </p>
                                            <p className="text-color-300 tracking-wider italic">
                                                {t('You can send {count} more requests today', {
                                                    count:  (
                                                        <span className="font-semibold text-color-100">
                                                            {5 - reset_pwd_attempts}
                                                        </span>
                                                    )
                                                })}
                                            </p>
                                        </div>
                                    ) : (
                                        <p className="text-sm text-color-100 tracking-wider">
                                            {t('You have performed this action 5 times. This feature has been temporarily locked for security reasons and will be available again in 24 hours',)}
                                        </p>
                                    )}

                                </div>
                            )}
                            <FieldDecorator
                                label={t('enter your email')}
                                isRequired={true}
                                labelProps={{className: undefined}}
                                errors={errors.email}>
                                <Input ift="email"
                                       inputMode="email"
                                       name="email"
                                       size="large"
                                       value={values.email}
                                       onChange={handleChange}
                                       onBlur={handleBlur}
                                       className={`ForgetPassword-control ${errors.email && 'ForgetPassword-control'}`}
                                       type="text"
                                       placeholder={t('Enter your email address')}
                                />
                            </FieldDecorator>
                            {renderIf(
                                success,
                                <div className="text-sm text-gray-500 italic tracking-wide leading-snug">
                                    {`* `} {t('the restore email has been sent to {email}. check your email and click on the link', {email: getEmail})}
                                </div>
                            )}
                            <div className="w-90 mx-auto flex flex-col items-center gap-3">
                                <Button
                                    disabled={
                                        reset_pwd_status === 1 ? timeLeft > 0 :
                                            reset_pwd_status === 2 ? reset_pwd_status !== 0 : false
                                    }
                                    block ift="resend"
                                    color="primary" variant="filled"
                                    loading={isLoading}
                                    className="no-shadow no-border px-3 py-1 font-semibold rounded-md tracking-wider leading-snug"
                                    onClick={handleSubmit}>
                                    {isLoading ? t('sending') : success ? t('resend') : t('send request')}
                                </Button>
                                <div
                                    ift="close"
                                    onClick={() => onBack()}
                                    style={{fontSize: 12, letterSpacing: '0.1em'}}
                                    className="text-color-400 hover:text-color-100 cursor-pointer px-3">
                                    {t('close')}
                                </div>
                            </div>
                        </form>
                    </div>
                )
            }}
        </Formik>
    )
}

export default ForgetPassword
